/**
 * Binary Field Editor Component
 *
 * Displays and manages field definitions for a binary format.
 * Shows accordion cards per field with conditional UI based on type.
 */

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Trash2,
  Plus,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TransformEditor } from './TransformEditor';
import { useBinaryFormatStore } from '@/stores/binary-formats/binary-format-store';
import {
  DATA_TYPE_INFO,
  dataTypeRequiresLength,
  dataTypeIsMultiByte,
  getDataTypeBytes,
} from '@/types/binaryFormat';
import type { BinaryField, DataType, Endianness } from '@/types/binaryFormat';

export const BinaryFieldEditor: React.FC = () => {
  const { currentFormat, updateField, deleteField, reorderFields } =
    useBinaryFormatStore();

  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const fields = currentFormat?.fields || [];

  const toggleField = (fieldId: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  };

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      reorderFields(index, index - 1);
    }
  };

  const handleMoveDown = (index: number) => {
    if (index < fields.length - 1) {
      reorderFields(index, index + 1);
    }
  };

  const getFieldSummary = (field: BinaryField): string => {
    const parts: string[] = [];
    const typeInfo = DATA_TYPE_INFO.find((t) => t.type === field.type);
    parts.push(typeInfo?.label || field.type);

    if (field.type === 'bits' && field.bitLength) {
      parts.push(`${field.bitLength} bits`);
    } else if (dataTypeRequiresLength(field.type) && field.length) {
      parts.push(`${field.length} bytes`);
    } else {
      const bytes = getDataTypeBytes(field.type);
      if (bytes) {
        parts.push(`${bytes} byte${bytes > 1 ? 's' : ''}`);
      }
    }

    if (field.transforms?.length) {
      parts.push(`${field.transforms.length} transform${field.transforms.length > 1 ? 's' : ''}`);
    }

    return parts.join(' · ');
  };

  if (fields.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No fields defined yet.</p>
        <p className="text-sm mt-1">Click "Add Field" to create your first field.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {fields.map((field, index) => (
        <Card key={field.id} className="overflow-hidden">
          <Collapsible
            open={expandedFields.has(field.id)}
            onOpenChange={() => toggleField(field.id)}
          >
            <CardHeader className="p-3">
              <CollapsibleTrigger asChild>
                <div className="flex items-center gap-2 cursor-pointer hover:bg-muted/30 rounded-md -m-1 p-1">
                  {/* Reorder Buttons */}
                  <div className="flex flex-col gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveUp(index);
                      }}
                      disabled={index === 0}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveDown(index);
                      }}
                      disabled={index === fields.length - 1}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>

                  <GripVertical className="h-4 w-4 text-muted-foreground" />

                  {/* Field Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs shrink-0">
                        @{field.offset}
                      </Badge>
                      <span className="font-medium truncate">
                        {field.name || '(unnamed)'}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {getFieldSummary(field)}
                    </div>
                  </div>

                  {/* Expand/Collapse Icon */}
                  {expandedFields.has(field.id) ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}

                  {/* Delete Button */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteField(field.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CollapsibleTrigger>
            </CardHeader>

            <CollapsibleContent>
              <CardContent className="pt-0 pb-4 px-4">
                <FieldConfigForm field={field} />
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      ))}
    </div>
  );
};

// ================================
// Field Configuration Form
// ================================

interface FieldConfigFormProps {
  field: BinaryField;
}

const FieldConfigForm: React.FC<FieldConfigFormProps> = ({ field }) => {
  const { updateField, currentFormat } = useBinaryFormatStore();
  const defaultEndian = currentFormat?.defaultEndian || 'BE';

  const handleChange = (updates: Partial<BinaryField>) => {
    updateField(field.id, updates);
  };

  const showLengthField = dataTypeRequiresLength(field.type);
  const showBitFields = field.type === 'bits';
  const showEndianField = dataTypeIsMultiByte(field.type);

  return (
    <div className="space-y-4">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`field-name-${field.id}`}>Field Name *</Label>
          <Input
            id={`field-name-${field.id}`}
            value={field.name}
            onChange={(e) => handleChange({ name: e.target.value })}
            placeholder="temperature"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`field-offset-${field.id}`}>Byte Offset *</Label>
          <Input
            id={`field-offset-${field.id}`}
            type="number"
            min="0"
            value={field.offset}
            onChange={(e) => handleChange({ offset: parseInt(e.target.value) || 0 })}
          />
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor={`field-desc-${field.id}`}>Description</Label>
        <Textarea
          id={`field-desc-${field.id}`}
          value={field.description || ''}
          onChange={(e) => handleChange({ description: e.target.value })}
          placeholder="Optional description of what this field represents..."
          rows={2}
          className="resize-none"
        />
      </div>

      {/* Data Type */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`field-type-${field.id}`}>Data Type *</Label>
          <Select
            value={field.type}
            onValueChange={(value) => handleChange({ type: value as DataType })}
          >
            <SelectTrigger id={`field-type-${field.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATA_TYPE_INFO.map((typeInfo) => (
                <SelectItem key={typeInfo.type} value={typeInfo.type}>
                  <div className="flex items-center gap-2">
                    <span>{typeInfo.label}</span>
                    <span className="text-xs text-muted-foreground">
                      ({typeInfo.description})
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Length (for variable-length types) */}
        {showLengthField && (
          <div className="space-y-2">
            <Label htmlFor={`field-length-${field.id}`}>Length (bytes) *</Label>
            <Input
              id={`field-length-${field.id}`}
              type="number"
              min="1"
              value={field.length || ''}
              onChange={(e) => handleChange({ length: parseInt(e.target.value) || undefined })}
              placeholder="e.g., 10"
            />
          </div>
        )}

        {/* Endianness (for multi-byte types) */}
        {showEndianField && (
          <div className="space-y-2">
            <Label htmlFor={`field-endian-${field.id}`}>Byte Order</Label>
            <Select
              value={field.endian || ''}
              onValueChange={(value) =>
                handleChange({ endian: value ? (value as Endianness) : undefined })
              }
            >
              <SelectTrigger id={`field-endian-${field.id}`}>
                <SelectValue placeholder={`Default (${defaultEndian})`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BE">Big Endian</SelectItem>
                <SelectItem value="LE">Little Endian</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Bit Field Settings */}
      {showBitFields && (
        <div className="grid grid-cols-2 gap-4 p-3 bg-muted/30 rounded-md">
          <div className="space-y-2">
            <Label htmlFor={`field-bitOffset-${field.id}`}>Bit Offset</Label>
            <Input
              id={`field-bitOffset-${field.id}`}
              type="number"
              min="0"
              max="7"
              value={field.bitOffset ?? 0}
              onChange={(e) => handleChange({ bitOffset: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-muted-foreground">
              Starting bit within the byte (0-7, LSB=0)
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`field-bitLength-${field.id}`}>Bit Length</Label>
            <Input
              id={`field-bitLength-${field.id}`}
              type="number"
              min="1"
              max="64"
              value={field.bitLength ?? 1}
              onChange={(e) => handleChange({ bitLength: parseInt(e.target.value) || 1 })}
            />
            <p className="text-xs text-muted-foreground">
              Number of bits to extract (1-64)
            </p>
          </div>
        </div>
      )}

      {/* Validation */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Validation</Label>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`field-required-${field.id}`}
              checked={field.validation?.required || false}
              onCheckedChange={(checked) =>
                handleChange({
                  validation: { ...field.validation, required: !!checked },
                })
              }
            />
            <Label htmlFor={`field-required-${field.id}`} className="text-sm font-normal">
              Required
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm font-normal whitespace-nowrap">Min:</Label>
            <Input
              type="number"
              className="w-20 h-8"
              value={field.validation?.min ?? ''}
              onChange={(e) =>
                handleChange({
                  validation: {
                    ...field.validation,
                    min: e.target.value ? parseFloat(e.target.value) : undefined,
                  },
                })
              }
              placeholder="—"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm font-normal whitespace-nowrap">Max:</Label>
            <Input
              type="number"
              className="w-20 h-8"
              value={field.validation?.max ?? ''}
              onChange={(e) =>
                handleChange({
                  validation: {
                    ...field.validation,
                    max: e.target.value ? parseFloat(e.target.value) : undefined,
                  },
                })
              }
              placeholder="—"
            />
          </div>
        </div>
      </div>

      {/* Default Value */}
      <div className="space-y-2">
        <Label htmlFor={`field-default-${field.id}`}>Default Value</Label>
        <Input
          id={`field-default-${field.id}`}
          value={String(field.defaultValue ?? '')}
          onChange={(e) =>
            handleChange({
              defaultValue: e.target.value ? e.target.value : undefined,
            })
          }
          placeholder="Value to use if extraction fails"
        />
      </div>

      {/* Transforms */}
      <div className="space-y-2 pt-2 border-t">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">
            Transforms {field.transforms?.length ? `(${field.transforms.length})` : ''}
          </Label>
        </div>
        <TransformEditor fieldId={field.id} transforms={field.transforms || []} />
      </div>
    </div>
  );
};

export default BinaryFieldEditor;
